/**
 * Field feature (plan G7): the instanced asteroid field through the asset lane's `AsteroidKit` (LOD buckets,
 * fieldView.ts) and the FR-217 "Harrow" derelict assembled from the `kit.station.*` models, with its static
 * trimesh collider handed to the sim (the view owns that one call because the collider comes from the build).
 *
 * Layout: the sim's own `field` when it was built for the active preset (every visible rock is solid: plan
 * §2.5), else the same deterministic `generateField` call the sim makes (field data, sim seed, preset, spawn
 * pocket). `bus 'quality'` rebuilds the kit and the instanced meshes for the new preset and emits nothing
 * (G13/G14 rebuild the colliders). Per frame: the field root and the derelict are placed in render space
 * (D-011) and the LOD buckets refresh when due. M2: rocks in `sim.removedRocks` are never drawn, and the buckets
 * are rewritten at once when `sim.fieldVersion` changed. Allocation-free per frame (D-014).
 *
 * Reach mode (M3 R1, plan §2.5): no derelict and no generation. The view draws whatever `sim.field` is (the
 * streamed rocks, R7/R12) and is rebuilt whenever `sim.field` becomes a different object (none while it is null;
 * the kit is kept and reused while the preset holds). The dust haze follows the drawn layout (its origin, with the
 * M1 envelope's largest semi-axis) and is left alone while there is no field, so the reach feature owns it then
 * (gas fields). The sandbox is unchanged.
 *
 * Rock tint (MC-a6b, tint.ts, `view.json tint`): when the view carries a tint, each drawn rock's instance colour
 * follows its field grade and the system's signature ore, read from M2's rock runtime (`RocksRuntime.fieldFacts`,
 * the per-rock grade/signature caches). It is refilled when the view is rebuilt or the rock runtime's field mix
 * lookup changes (`fieldMixVersion`, a system entry); per frame that is two comparisons. Without M2, or for a
 * layout that is not the sim's, every rock keeps the identity tint.
 */
import type { LoadedModel } from '../../assets/loader'
import type { FrameInfo, GameContext, GameFeature } from '../../app/feature'
import { FEATURE_ORDER } from '../../app/feature'
import { logger } from '../../core/logger'
import type { Vec3 } from '../../core/math'
import { addDerelictCollider, removeStaticColliders, type StaticColliders } from '../../physics/statics'
import type { QualityPreset } from '../../render/contracts'
import { Vector3 } from 'three'
import { buildDerelict, createAsteroidKit, createShadowBox, enableShadows, SHADOW_NEAR_LEVELS, type AsteroidKit, type DerelictBuild, type ShadowBox } from '../../render/environment'
import { toRenderSpace } from '../../render/floatingOrigin'
import type { FieldConfig } from '../../data/schemas'
import type { MaterialLibrary } from '../../render/materials'
import { derelictPose, generateField, type DerelictPose, type FieldLayout } from '../../world/field'
import { loadFieldViewConfig, type FieldViewConfig } from './config'
import { createFieldView, useCasterShadows, type FieldView } from './fieldView'
import { uniformBufferLimit } from '../../render/uploads'
import { rockTint, signatureColors, type RockTintConfig } from './tint'
import type { M2Runtime, RockFieldFacts } from '../../space/contracts'

/** What the feature builds its rocks with (tests pass fakes). */
export interface FieldFactories {
  createKit(lib: MaterialLibrary, seed: number, preset: QualityPreset): AsteroidKit
  createView(kit: AsteroidKit, layout: FieldLayout, config: FieldViewConfig, removed?: ReadonlySet<number>, casters?: boolean, near?: boolean, uniformLimit?: number): FieldView
}

const DEFAULT_FACTORIES: FieldFactories = { createKit: createAsteroidKit, createView: createFieldView }

/** The feature plus read access for tests and the dev tools. */
export interface FieldFeature extends GameFeature {
  readonly view: FieldView | null
  readonly kit: AsteroidKit | null
  readonly derelict: DerelictBuild | null
  readonly derelictPose: DerelictPose | null
  readonly derelictColliders: StaticColliders | null
  frame(ctx: GameContext, f: FrameInfo): void
  dispose(): void
}

/** The station modules the derelict is assembled from, by asset id. */
export function stationKitOf(models: ReadonlyMap<string, LoadedModel>): Map<string, LoadedModel> {
  const kit = new Map<string, LoadedModel>()
  for (const [id, model] of models) if (model.entry.class === 'station_module' || id.startsWith('kit.station.')) kit.set(id, model)
  return kit
}

/** The layout for `preset`: the sim's when it matches, else the identical deterministic generation. */
function layoutFor(ctx: GameContext, preset: QualityPreset): FieldLayout {
  const sim = ctx.sim
  if (sim.field && sim.preset === preset) return sim.field
  const p = ctx.data.world.spawn.position
  return generateField(ctx.data.field, sim.seed, preset, { x: p[0], y: p[1], z: p[2] })
}

/** Disposes what the old kit owned and the new one does not reuse (an implementation may cache). */
function disposeKit(old: AsteroidKit, next: AsteroidKit): void {
  for (let a = 0; a < old.geometries.length; a++) {
    for (let l = 0; l < old.geometries[a].length; l++) {
      const g = old.geometries[a][l]
      if (!next.geometries.some((row) => row.includes(g))) g.dispose()
    }
  }
  for (let a = 0; a < old.materials.length; a++) if (!next.materials.includes(old.materials[a])) old.materials[a].dispose()
}

/** Disposes everything a kit owns (reach mode: a preset change with no field drawn). */
function releaseKit(kit: AsteroidKit): void {
  for (let a = 0; a < kit.geometries.length; a++) for (let l = 0; l < kit.geometries[a].length; l++) kit.geometries[a][l].dispose()
  for (let a = 0; a < kit.materials.length; a++) kit.materials[a].dispose()
}

interface FieldState {
  view: FieldView | null
  kit: AsteroidKit | null
  /** The preset `kit` was built for (reach mode reuses the kit across layouts while it holds). */
  kitPreset: QualityPreset | null
  derelict: DerelictBuild | null
  derelictPose: DerelictPose | null
  derelictColliders: StaticColliders | null
  /** `sim.fieldVersion` the view last bucketed with. */
  fieldVersion: number
  /** The view and rock-runtime mix version the tint was last filled for (MC-a6b). */
  tintView: FieldView | null
  tintVersion: number
}

// Module scratch (D-014): one rock's field facts while the tint is filled.
const facts: RockFieldFacts = { grade: 0, signature: -1 }
/** Signature colours per ware (tint.ts), cached per M2 wares table (load-time). */
let sigColors: Float32Array = new Float32Array(0)
let sigColorsOf: unknown = null

/** The luminance-normalised ware tints of `m2`'s wares table, built once per table. */
function signatureColorsOf(m2: M2Runtime): Float32Array {
  const wares = m2.data.wares.wares
  if (sigColorsOf !== wares) {
    sigColors = signatureColors(wares.map((w) => w.tintHex))
    sigColorsOf = wares
  }
  return sigColors
}

/**
 * Fills the view's rock tint from M2's rock caches (identity without M2, or when the view draws a layout that is not
 * the sim's), then rewrites the buckets. Rare (a rebuilt view or a new field mix lookup); allocation-free once the
 * signature colours are cached.
 */
function fillTint(view: FieldView, tints: Float32Array, cfg: RockTintConfig, m2: M2Runtime | undefined, simField: FieldLayout | null, cam: Readonly<Vec3>): void {
  const rocks = m2 === undefined || view.layout !== simField ? undefined : m2.space.rocks
  const colors = m2 === undefined ? sigColors : signatureColorsOf(m2)
  for (let i = 0; i < view.layout.count; i++) {
    if (rocks === undefined || rocks.fieldFacts === undefined || !rocks.fieldFacts(i, facts)) {
      facts.grade = 0
      facts.signature = -1
    }
    rockTint(facts.grade, facts.signature, cfg, colors, tints, i * 3)
  }
  view.rebucket(cam, true)
}

/** Per frame: refills the tint when the view or the rock runtime's field mix lookup changed. Allocation-free. */
function syncTint(state: FieldState, cfg: RockTintConfig, ctx: GameContext): void {
  const view = state.view
  if (view === null || view.tints === null) return
  const m2 = ctx.services.maybe('m2')
  const version = m2 === undefined ? -1 : (m2.space.rocks.fieldMixVersion ?? -1)
  if (view === state.tintView && version === state.tintVersion) return
  state.tintView = view
  state.tintVersion = version
  fillTint(view, view.tints, cfg, m2, ctx.sim.field, ctx.rig.pos)
}

// Module scratch (D-014): the shadow level-1 box the near rock set culls against (PF-2b).
const nearBoxScratch: ShadowBox = createShadowBox()

/** PF-2b: the finest level the near set feeds (level 1) from the environment, or null with no active level. */
function nearBoxOf(ctx: GameContext): ShadowBox | null {
  return ctx.env.shadowBox(SHADOW_NEAR_LEVELS - 1, nearBoxScratch) ? nearBoxScratch : null
}

// Module scratch (D-014): the dust volume centre in render space.
const dustCenter = new Vector3()

/** The field envelope as the dust haze sphere (D-047): its centre into render space, its largest semi-axis. */
function frameDust(ctx: GameContext, field: FieldConfig): void {
  const c = field.envelope.center
  const s = field.envelope.semiAxes
  toRenderSpace(c[0], c[1], c[2], ctx.rig.pos, dustCenter)
  ctx.env.setDustVolume(dustCenter, Math.max(s[0], s[1], s[2]))
}

/** Reach mode, per frame (allocation-free): the dust haze around the drawn layout, sized like the M1 field's. */
function frameReachDust(ctx: GameContext, layout: FieldLayout, field: FieldConfig): void {
  const o = layout.origin
  const s = field.envelope.semiAxes
  toRenderSpace(o.x, o.y, o.z, ctx.rig.pos, dustCenter)
  ctx.env.setDustVolume(dustCenter, Math.max(s[0], s[1], s[2]))
}

/**
 * Per frame (allocation-free): field root and derelict into render space, the caster range from the live shadow
 * reach (PF-2) and the near set's level-1 box (PF-2b; null = no active level), then the LOD buckets when due, or at once when the sim's removed rocks changed (`fieldVersion`).
 */
function frameField(state: FieldState, cam: Readonly<Vec3>, fieldVersion: number, shadowReach: number, nearBox: Readonly<ShadowBox> | null): void {
  const view = state.view
  if (view) {
    view.setCasterReach(shadowReach)
    view.setNearBox(nearBox)
    const o = view.layout.origin
    toRenderSpace(o.x, o.y, o.z, cam, view.root.position)
    if (fieldVersion !== state.fieldVersion) {
      state.fieldVersion = fieldVersion
      view.rebucket(cam, true)
    } else {
      view.update(cam)
    }
  }
  const d = state.derelict
  const pose = state.derelictPose
  if (d && pose) toRenderSpace(pose.position.x, pose.position.y, pose.position.z, cam, d.root.position)
}

export function createFieldFeature(
  config: FieldViewConfig = loadFieldViewConfig(),
  factories: FieldFactories = DEFAULT_FACTORIES,
): FieldFeature {
  let ctxRef: GameContext | null = null
  let unsubscribe: (() => void) | null = null
  /** The preset the view should use (reach mode builds lazily, when a layout appears). */
  let preset: QualityPreset = 'high'

  const state: FieldState = {
    view: null,
    kit: null,
    kitPreset: null,
    derelict: null,
    derelictPose: null,
    derelictColliders: null,
    fieldVersion: 0,
    tintView: null,
    tintVersion: -1,
  }

  function removeView(ctx: GameContext): void {
    if (!state.view) return
    ctx.scene.remove(state.view.root)
    state.view.dispose()
    state.view = null
  }

  /**
   * Builds the view of `layout` at `p` (null: no view). A new kit unless `reuseKit` and the kit already matches the
   * preset (the sandbox always rebuilds it, as in M1).
   */
  function buildField(ctx: GameContext, p: QualityPreset, layout: FieldLayout | null, reuseKit: boolean): void {
    if (layout === null) {
      removeView(ctx)
      return
    }
    const reuse = reuseKit && state.kit !== null && state.kitPreset === p
    const kit = reuse && state.kit ? state.kit : factories.createKit(ctx.lib, ctx.sim.seed, p)
    const view = factories.createView(kit, layout, config, ctx.sim.removedRocks, config.shadowCasters.enabled[p], config.shadowCasters.nearSet[p], uniformBufferLimit(ctx.renderer))
    removeView(ctx)
    if (state.kit && state.kit !== kit) disposeKit(state.kit, kit)
    state.kit = kit
    state.kitPreset = p
    state.view = view
    ctx.scene.add(view.root)
    // The star key light shadows the rocks (D-047); a rebuilt kit needs its new buckets flagged again.
    enableShadows(view.root)
    // PF-2: the coarse caster set casts instead of the drawn buckets (when the preset has it on).
    useCasterShadows(view)
    state.fieldVersion = ctx.sim.fieldVersion
    syncTint(state, config.tint, ctx)
    frameField(state, ctx.rig.pos, ctx.sim.fieldVersion, ctx.env.shadowReach(), nearBoxOf(ctx))
    logger.info(`Asteroid field (${p}): ${view.layout.count} rocks`)
  }

  /** Reach mode: a preset change rebuilds the drawn layout with a new kit, or drops the kit when nothing is drawn. */
  function reachQuality(ctx: GameContext, p: QualityPreset): void {
    preset = p
    if (state.view) {
      buildField(ctx, p, state.view.layout, false)
      return
    }
    if (state.kit) releaseKit(state.kit)
    state.kit = null
    state.kitPreset = null
  }

  /** Reach mode: rebuild for the sim's current layout (a rare event: `sim.field` changed identity). */
  function followSimField(ctx: GameContext): void {
    buildField(ctx, preset, ctx.sim.field, true)
  }

  function buildDerelictView(ctx: GameContext): void {
    const stationKit = stationKitOf(ctx.models)
    if (stationKit.size === 0) {
      logger.warn('No kit.station models loaded: the Harrow derelict is skipped')
      return
    }
    const build = buildDerelict(ctx.lib, stationKit, ctx.sim.seed)
    const pose = derelictPose(ctx.data.field)
    const q = pose.rotation
    build.root.quaternion.set(q.x, q.y, q.z, q.w)
    toRenderSpace(pose.position.x, pose.position.y, pose.position.z, ctx.rig.pos, build.root.position)
    ctx.scene.add(build.root)
    state.derelict = build
    state.derelictPose = pose
    if (build.collider.indices.length < 3) {
      logger.warn('The Harrow derelict has no collider triangles: it stays non-solid')
      return
    }
    state.derelictColliders = addDerelictCollider(ctx.sim.physics, build.collider, pose)
  }

  const feature: FieldFeature = {
    name: 'field',
    order: FEATURE_ORDER.field,
    get view(): FieldView | null {
      return state.view
    },
    get kit(): AsteroidKit | null {
      return state.kit
    },
    get derelict(): DerelictBuild | null {
      return state.derelict
    },
    get derelictPose(): DerelictPose | null {
      return state.derelictPose
    },
    get derelictColliders(): StaticColliders | null {
      return state.derelictColliders
    },
    init(ctx: GameContext): void {
      ctxRef = ctx
      preset = ctx.quality
      if (ctx.reach !== null) {
        followSimField(ctx)
        unsubscribe = ctx.bus.on('quality', (p) => reachQuality(ctx, p))
        return
      }
      buildField(ctx, ctx.quality, layoutFor(ctx, ctx.quality), false)
      buildDerelictView(ctx)
      unsubscribe = ctx.bus.on('quality', (p) => buildField(ctx, p, layoutFor(ctx, p), false))
    },
    frame(ctx: GameContext): void {
      if (ctx.reach !== null) {
        const view = state.view
        if (ctx.sim.field !== (view === null ? null : view.layout)) followSimField(ctx)
        syncTint(state, config.tint, ctx)
        frameField(state, ctx.rig.pos, ctx.sim.fieldVersion, ctx.env.shadowReach(), nearBoxOf(ctx))
        if (state.view !== null) frameReachDust(ctx, state.view.layout, ctx.data.field)
        return
      }
      syncTint(state, config.tint, ctx)
      frameField(state, ctx.rig.pos, ctx.sim.fieldVersion, ctx.env.shadowReach(), nearBoxOf(ctx))
      frameDust(ctx, ctx.data.field)
    },
    dispose(): void {
      unsubscribe?.()
      unsubscribe = null
      const ctx = ctxRef
      if (state.view) {
        ctx?.scene.remove(state.view.root)
        state.view.dispose()
      }
      if (state.derelict) ctx?.scene.remove(state.derelict.root)
      if (ctx && state.derelictColliders) removeStaticColliders(ctx.sim.physics, state.derelictColliders)
      ctx?.env.setDustVolume(dustCenter.set(0, 0, 0), 0)
      state.view = null
      state.kit = null
      state.kitPreset = null
      state.derelict = null
      state.derelictPose = null
      state.derelictColliders = null
      state.tintView = null
      state.tintVersion = -1
      ctxRef = null
    },
  }
  return feature
}

const feature: FieldFeature = createFieldFeature()
export default feature
